import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactElement,
} from "react";
import { Button, EmptyState, Input, Wordmark } from "@playbookai/ui";
import { PRODUCT_NAME } from "@playbookai/types";

import {
  PasswordRejectedError,
  SetPasswordNotUsableError,
  SetPasswordOutcomeUnknownError,
  SetPasswordRequestFailedError,
  completeSetPassword,
  previewSetPassword,
  type SetPasswordPreview,
} from "../../api/set-password-api.js";

/**
 * PB-5294. Mounted on `/set-password`, OUTSIDE the auth guard.
 *
 * The visitor has no session and no credential — that is the entire point — so
 * this renders standalone exactly as `InvitePage` and `CallbackPage` do: no
 * `AppLayout`, no top bar, no tab strip.
 *
 * NOTHING TERMINAL NAVIGATES, including on success. Navigating away mounts
 * `RequireAuth`, which begins a login the visitor may be unable to complete,
 * turning one failure into a redirect loop whose recorded consequence is a
 * Keycloak 429 that locks them out of the very account being handed to them.
 * `InvitePage` carries a test asserting its success screen has zero buttons
 * for this reason; the same rule applies here.
 *
 * THE TOKEN COMES FROM `location.hash`, NOT THE PATH, and that is the one real
 * divergence from `/invite/:token`. A fragment is never transmitted in an HTTP
 * request and is stripped from `Referer`, so it cannot reach nginx, a proxy or
 * a downstream log. The path form is measurably leaking today:
 * `nginx.conf.template` declares no `log_format`, so the image default
 * `combined` applies and logs `$http_referer`; `Referrer-Policy` is
 * `strict-origin-when-cross-origin`, which sends the full URL same-origin; and
 * `location ^~ /assets/` has no `access_log off`. Every first load of an
 * invite link therefore writes its raw token to pod stdout.
 *
 * The token is never written to storage. `localStorage` and `sessionStorage`
 * are forbidden for security-relevant state, and this is the only credential
 * the visitor holds.
 */
/**
 * The fragment, captured ONCE PER DOCUMENT LOAD rather than once per mount.
 *
 * This page reads its token from `location.hash` and then erases the hash, so
 * the value is readable exactly once. That made it fatally dependent on being
 * mounted exactly once, and it is not: the shell remounts this route when the
 * session query settles, and React 18 StrictMode re-invokes initialisers in
 * development. The second instance read a hash the first had already cleared,
 * concluded it had no token, and rendered the terminal "this link is not valid"
 * screen — over a perfectly good link whose preview had already answered 200.
 *
 * Only a browser could see it. Every RTL test mounts once, so the second read
 * never happens, and all seventeen passed throughout.
 *
 * Module-scoped, not a ref: a ref is recreated by the very remount this exists
 * to survive. Lazy, not at import time, so importing the route has no side
 * effect. Per document rather than per session — though a document load is NOT
 * the only way a genuinely new link arrives: pasted into the tab that is
 * already open it is a fragment-only navigation that loads nothing at all, so
 * `adoptFragmentToken` below retires this capture when one turns up.
 *
 * `InvitePage` needs none of this: its token is a path segment that
 * `useParams` re-derives identically on every mount.
 */
let capturedFragmentToken: string | undefined;

function readFragmentToken(): string {
  const raw = window.location.hash.replace(/^#/, "");
  try {
    /* A mail client may percent-encode the fragment even though the token is
       base64url. */
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

function captureFragmentToken(): string {
  if (capturedFragmentToken === undefined) {
    capturedFragmentToken = readFragmentToken();
  }
  return capturedFragmentToken;
}

/**
 * A token that arrived AFTER the document loaded, or null if nothing new did.
 *
 * The capture above is per document, which is right for a remount and wrong
 * for the one flow this whole feature exists to serve: the first link died, an
 * administrator copied a fresh one out of the re-send modal, and the visitor
 * pastes it into the tab that is already open. Because this page erases the
 * hash on mount, the address bar reads `/set-password`, so pasting
 * `/set-password#tokenB` is a fragment-only, SAME-DOCUMENT navigation. The
 * browser loads nothing, the capture is never re-read, and the visitor is
 * refused on the strength of the dead token they were given first.
 *
 * Narrow on purpose, so it cannot damage the property the capture exists for.
 * An empty fragment is IGNORED rather than adopted — this page erases the hash
 * itself, and a `replaceState` fires no `hashchange`, but a Back, a bare `#`
 * link or a future edit could, and adopting "" would blank the cache and hand
 * the next remount the dead-link screen. An unchanged fragment is ignored too:
 * re-pasting the same link says nothing new, so the answer already on screen
 * stands.
 */
function adoptFragmentToken(): string | null {
  const next = readFragmentToken();
  if (next.length === 0 || next === capturedFragmentToken) return null;
  capturedFragmentToken = next;
  return next;
}

/**
 * Take the token back out of the address bar.
 *
 * `replaceState` rather than `pushState` so Back does not walk into a URL whose
 * token has since been consumed — and, usefully here, because `replaceState`
 * fires no `hashchange` and so cannot feed the listener below its own output.
 */
function eraseFragment(): void {
  if (!window.location.hash) return;
  window.history.replaceState(
    null,
    "",
    `${window.location.pathname}${window.location.search}`,
  );
}

/** Test-only. Each test stands in for a fresh document; the cache must not
    outlive one, or a later case inherits an earlier case's token. */
export function resetCapturedFragmentToken(): void {
  capturedFragmentToken = undefined;
}

export function SetPasswordPage(): ReactElement {
  /* Held only in component state, never in storage — see the file header. */
  const [token, setToken] = useState(captureFragmentToken);

  const [preview, setPreview] = useState<SetPasswordPreview | null>(null);
  /* `"dead"` is the server's single refusal — terminal, nothing the visitor
     can do. `"unreachable"` is a non-answer: a 503 while the service
     restarts, a 429, a dropped connection, or the rollout window in which
     this page is deployed and the endpoint is not. Collapsing the two would
     tell somebody holding a perfectly good link to go and ask for another. */
  const [loadFailure, setLoadFailure] = useState<null | "dead" | "unreachable">(
    null,
  );
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  /* The SUBMIT-time twin of `loadFailure === "dead"`, held separately because
     `loadFailure` describes the PREVIEW and is written only by `loadPreview` —
     whose `setLoadFailure(null)` on entry would otherwise read as though it
     could clear a submit-time refusal. `SetPasswordNotUsableError` from
     `completeSetPassword` is the same single refusal arriving one step later:
     the token was consumed, superseded or expired between the preview and this
     POST. Nothing the visitor can type makes a second attempt work, so the form
     has to GO — an inline message left it enabled and invited them to keep
     re-submitting a credential that is already spent. */
  const [linkDied, setLinkDied] = useState(false);
  /* Held apart from `linkDied` because the two say opposite things about the
     password. `linkDied` means the token was already spent and nothing was
     written; this means the write may have LANDED and nobody can say. */
  const [outcomeUnknown, setOutcomeUnknown] = useState<string | null>(null);
  /* Guards React 18 StrictMode's double mount, as `InvitePage` does. The
     preview is read-only so a second fetch is harmless, but it is still a
     second unauthenticated request from a page whose job is to make as few as
     possible. */
  const previewInFlight = useRef(false);
  /* THE TOKEN EVERY IN-FLIGHT ANSWER MUST STILL BE ABOUT.
     Both requests this page makes are started for one particular token and
     answer some time later, and by then the token may have been replaced —
     the paste handler below is the re-send flow's own ending, so a reply
     arriving about a token nobody is holding any more is the NORMAL path here,
     not an exotic race. Worse, in that flow the first token has just been
     superseded, so `SetPasswordNotUsableError` is its EXPECTED answer: with
     nothing binding a reply to its question, the old link's refusal landed as
     the terminal "This link is not valid." screen — which carries no retry
     affordance at all — on top of the live link the visitor had only just
     pasted. The quieter half of the same shape is a late FULFILMENT, which
     named the previous workspace and email above a form about to submit the
     new token.
     A ref rather than state because these writes must be judged against the
     token that is current AT THE MOMENT THEY LAND, not against whatever the
     render that started them closed over. */
  const currentToken = useRef(token);

  /* Clear the fragment once it is in state.
     The token has already done its only job — reaching this component — and
     leaving it in the address bar keeps a live credential where a screenshot,
     a shoulder, a shared screen or the browser's own history can pick it up.
     It cannot break the read above: that runs once, in a `useState`
     initialiser, before this effect fires. */
  useEffect(() => {
    if (!token) return;
    eraseFragment();
  }, [token]);

  const loadPreview = useCallback((): void => {
    if (!token) return;
    /* Named rather than read back out of the closure at answer time, so the
       two guards below plainly compare against the token THIS request asked
       about. */
    const issuedFor = token;
    setLoadFailure(null);
    previewSetPassword(issuedFor)
      .then((answer) => {
        if (currentToken.current !== issuedFor) return;
        setPreview(answer);
      })
      .catch((error: unknown) => {
        if (currentToken.current !== issuedFor) return;
        /* ONE state for every refusal — consumed, superseded, expired and
           unknown must stay indistinguishable, or the page rebuilds the
           oracle the server was careful not to give. What IS separated is a
           refusal from a non-answer. */
        setLoadFailure(
          error instanceof SetPasswordNotUsableError ? "dead" : "unreachable",
        );
      });
  }, [token]);

  useEffect(() => {
    if (previewInFlight.current) return;
    previewInFlight.current = true;
    loadPreview();
  }, [loadPreview]);

  /* Start over when a SECOND link is pasted into this same tab.
     That is not an exotic path: it is the re-send flow's own ending. The first
     link died, an administrator sent another, and the visitor pastes it where
     they already are. The hash has been erased by the effect above, so the
     browser treats it as a fragment-only navigation — no load, no re-read —
     and without this the page answers about the token it captured minutes ago.

     Every verdict on the OLD token is cleared, because each of them would
     otherwise sit on top of a perfectly good new one: the submit-time
     dead-link screen, the outcome-unknown screen, the success screen, the
     inline failure and the stale preview. `submitting` belongs to that list
     too, even though it is a verdict nobody has reached yet — the old token's
     POST is still out there, its answer is about to be discarded by the
     binding above, and leaving the flag set would strand the new link's form
     permanently on "Setting your password…". The preview-time `loadFailure` is
     NOT cleared here — `loadPreview` opens by clearing it, so a second line
     would be one no test could ever fail on. `previewInFlight` is released as
     well: it is the StrictMode double-mount guard, and leaving it latched
     would suppress the very preview this new token needs. The typed names and
     password are left alone, since a re-sent link almost always reaches the
     same person, who should not have to fill the form in twice.

     No dependencies. The handler reads only setters, the ref and the module
     capture, all of which outlive any render, so the listener is registered
     once and is never torn down mid-paste. */
  useEffect(() => {
    function onFragmentReplaced(): void {
      const next = adoptFragmentToken();
      /* After the READ and before the branch. Whatever was just pasted is a
         live credential sitting in the address bar, and that is as true of a
         link the page declines to restart on — a re-paste of the one already
         open — as it is of a new one, so the erase cannot live inside the
         branch below. The effect above cannot cover the re-paste either: its
         dependency is the token, which a re-paste does not change. */
      eraseFragment();
      if (next === null) return;
      /* Retired SYNCHRONOUSLY, before any of the state updates below. A reply
         already in flight settles as a microtask, which can run long before
         React commits this render, so the guards it meets have to already know
         the token has moved on. */
      currentToken.current = next;
      setToken(next);
      setPreview(null);
      setLinkDied(false);
      setOutcomeUnknown(null);
      setFailure(null);
      setDone(false);
      setSubmitting(false);
      previewInFlight.current = false;
    }
    window.addEventListener("hashchange", onFragmentReplaced);
    return () => window.removeEventListener("hashchange", onFragmentReplaced);
  }, []);

  const mismatch = confirm.length > 0 && password !== confirm;
  const ready =
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    password.length > 0 &&
    password === confirm;

  const submit = async (): Promise<void> => {
    if (!token || submitting || !ready) return;
    /* Bound exactly as the preview is, and the stakes here are higher: the
       success screen, the terminal dead-link screen and the outcome-unknown
       screen are all TERMINAL, so a verdict about a replaced token ends the
       journey for the live one — telling the visitor their password is set
       when this link has not been used, or that the link is dead when it is
       not. */
    const issuedFor = token;
    setSubmitting(true);
    setFailure(null);
    try {
      await completeSetPassword({
        setPasswordToken: issuedFor,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        password,
      });
      if (currentToken.current !== issuedFor) return;
      setDone(true);
    } catch (error) {
      if (currentToken.current !== issuedFor) return;
      if (error instanceof PasswordRejectedError) {
        /* Keycloak's own wording, verbatim. The realm is the authority on what
           it refused (AC-5) and paraphrasing here would be wrong the moment an
           administrator changes the policy. */
        setFailure(error.message);
      } else if (error instanceof SetPasswordNotUsableError) {
        /* Deliberately NOT `setFailure`: that paragraph renders inside the form
           branch, beneath a live submit button, and this state replaces the
           whole branch. A terminal refusal is a SCREEN on this page, never a
           note beside an affordance that cannot work. */
        setLinkDied(true);
      } else if (error instanceof SetPasswordOutcomeUnknownError) {
        /* The one failure where "your password was not changed" would be a
           LIE. Auth reached Keycloak, could not learn whether the credential
           landed, and therefore spent the link rather than risk one that
           outlives the password it wrote. The server's own sentence tells them
           to try signing in first, which is the only thing that can actually
           settle it — so it is passed through verbatim rather than paraphrased
           into a retry that may be unnecessary. Terminal: the link is gone. */
        setOutcomeUnknown(error.message);
      } else if (
        error instanceof SetPasswordRequestFailedError &&
        error.status === 400
      ) {
        /* The service ANSWERED, and said the body was malformed. The generic
           copy below claims the opposite and then advises the one thing that
           cannot help — re-sending the identical value.

           Reachable through an ordinary typo, not a broken client. The
           controller's `PASSWORD_MIN` is 8, so a one-to-seven-character
           password is a 400 here, while eight to eleven characters reaches
           Keycloak and comes back as a 422 carrying the realm's own sentence.
           Two adjacent inputs, opposite answers, and the worse one was the
           shorter password. A long name is the same class: over `NAME_MAX` and
           the server refuses the shape rather than the content.

           NO NUMBER AND NO RULE IN THIS COPY, deliberately. The realm's actual
           minimum is twelve and Keycloak remains the authority on it (AC-5);
           naming eight here would advertise a length the workspace will refuse,
           which is a second untruth rather than a fix. All this sentence
           claims is what the 400 actually proves: every `read*` guard runs
           before the rate-limit budget and before the Keycloak call, so
           nothing was written, and the value has to CHANGE before it can be
           sent again. Distinguishing the two is exactly why the service chose
           422 over 400 for a policy refusal. */
        setFailure(
          "Those details weren't accepted, and your password was not changed. Try a longer password, and check both names.",
        );
      } else {
        setFailure(
          "The service didn't answer. Your password was not changed — try again in a moment.",
        );
      }
    } finally {
      /* Bound like every other write, and not merely for tidiness. The paste
         handler has already cleared `submitting` for the new token, so this
         line can only ever re-arm a button that belongs to somebody else's
         request — and if the visitor has by then submitted the NEW link, that
         hands them a second POST which spends the token their first one is
         still consuming, ending on the terminal screen over a password that
         was in fact set. */
      if (currentToken.current === issuedFor) setSubmitting(false);
    }
  };

  if (loadFailure === "unreachable") {
    /* A retry button HERE, where the terminal screen deliberately has none:
       the link may be perfectly good and the service merely busy, so the
       affordance can actually work. That is the same distinction `InvitePage`
       draws, and the reason both branches exist separately. */
    /* The page-level status screen: EmptyState lg, error (OK-32). */
    return (
      <Standalone role="alert">
        <EmptyState
          variant="error"
          size="lg"
          title="We couldn't check your link."
          description="The service didn't answer. Your link may still be fine — try again in a moment."
          action={
            <Button variant="secondary" onClick={loadPreview}>
              Try again
            </Button>
          }
        />
      </Standalone>
    );
  }

  if (outcomeUnknown) {
    /* Its own screen, not the invalid-link one: that screen tells the visitor
       the link was never going to work and to fetch another, and here the
       password they just chose may already be live. No retry affordance —
       the link is spent either way, so the only useful next step is the one
       the message names. */
    /* The page-level status screen: EmptyState lg, error (OK-32). */
    return (
      <Standalone role="alert">
        <EmptyState
          variant="error"
          size="lg"
          title="We couldn't confirm your password."
          description={outcomeUnknown}
        />
      </Standalone>
    );
  }

  if (!token || loadFailure === "dead" || linkDied) {
    /* No retry affordance at all. Nothing the visitor does can revive a spent
       or superseded token, and an affordance that cannot work is worse than
       none. The instruction names a real action someone can take for them —
       which, unlike before this change, now exists.

       Reached from two places, and the extra line answers the question only
       the second one raises. Dying at PREVIEW means the form never appeared.
       Dying at SUBMIT means the visitor has just typed a password and pressed
       a button, so the first thing they need to know is whether it took — and
       it did not: every path that throws this refusal runs BEFORE the first
       Keycloak write, and the transaction rolls back whole. */
    /* The page-level status screen: EmptyState lg, error (OK-32). */
    return (
      <Standalone role="alert">
        <EmptyState
          variant="error"
          size="lg"
          title="This link is not valid."
          description={
            <>
              {linkDied ? <p>Your password was not changed.</p> : null}
              <p>
                It may have already been used, or it may have expired. Ask an
                admin of your workspace to send you a new one.
              </p>
            </>
          }
        />
      </Standalone>
    );
  }

  if (done) {
    /* No sign-in button, deliberately, and for the reason this whole page
       documents: navigating mounts `RequireAuth`. The password is now set and
       the profile is complete, so an ordinary sign-in genuinely works — the
       visitor simply has to start it themselves. */
    /* The page-level success screen: EmptyState lg, healthy (OK-32). */
    return (
      <Standalone>
        <EmptyState
          tone="healthy"
          size="lg"
          title="Your password is set."
          description={
            <>
              You can now sign in to{" "}
              {preview?.workspaceName ?? "your workspace"} with your email
              address and the password you just chose.
            </>
          }
        />
      </Standalone>
    );
  }

  if (!preview) {
    /* Kept as plain text: a Spinner ring beside it is a look change no PR 3
       row approves (OK-38 names other files). */
    return (
      <Standalone>
        <p>Checking your link…</p>
      </Standalone>
    );
  }

  return (
    <Standalone>
      <p className="text-lg font-medium text-pb-text">
        Set your password for {preview.workspaceName}
      </p>
      {preview.email ? (
        <p className="max-w-md text-sm">
          You&apos;re setting the password for {preview.email}.
        </p>
      ) : null}
      {/* A REAL form, so Enter submits — `packages/ui`'s Button defaults to
          `type="button"`, which would otherwise leave the keyboard with no way
          to submit at all.

          `noValidate` is not incidental. Native HTML validation fires BEFORE
          the submit handler and shows its own bubble, which would pre-empt the
          workspace's actual policy message — and AC-5 is specifically that
          Keycloak's reason is what the person sees. */}
      <form
        className="contents"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        {/* AC-12. Both names, and they are not optional politeness: the OIDC
            callback refuses a local profile missing either, so an account
            created without them cannot be signed into at all. Nothing else in
            the invited journey collects them — `createInvitedLocalUser` writes
            neither and the realm's VERIFY_PROFILE action is disabled — so this
            is the last place they can be asked for. */}
        {/* `text-left` because the standalone frame centres everything, which
            is right for its one-line messages and wrong for a stacked form:
            four centred labels sitting above four full-width inputs read as
            captions rather than labels. Found in a browser — the unit tests
            pass either way, which is exactly the class of defect they cannot
            see. */}
        <div className="flex w-full max-w-md flex-col gap-3 text-left">
          <Input
            label="First name"
            autoComplete="given-name"
            value={firstName}
            onChange={(event) => setFirstName(event.currentTarget.value)}
          />
          <Input
            label="Last name"
            autoComplete="family-name"
            value={lastName}
            onChange={(event) => setLastName(event.currentTarget.value)}
          />
          <Input
            label="Password"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(event) => setPassword(event.currentTarget.value)}
          />
          <Input
            label="Confirm password"
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(event) => setConfirm(event.currentTarget.value)}
          />
        </div>
        {/* The ONLY rule this page enforces itself. Everything about the
            password's strength belongs to the realm (AC-5) — re-implementing
            a minimum here would be a second policy to drift out of step with
            the first. A typo in the confirmation field is not a policy
            question, and catching it locally saves a round trip. */}
        {/* Kept as today's centred line: Input `error` moves the message
            under the field in red dense type, and Alert adds a glyph; no PR 3
            look row approves either. */}
        {mismatch ? (
          <p className="max-w-md text-sm" role="alert">
            Those two passwords don&apos;t match.
          </p>
        ) : null}
        <Button type="submit" variant="primary" disabled={submitting || !ready}>
          {submitting ? "Setting your password…" : "Set password"}
        </Button>
      </form>
      {/* Kept as plain text: Alert adds a box or a glyph, a look change no
          PR 3 row approves. */}
      {failure ? (
        <p className="max-w-md text-sm" role="alert">
          {failure}
        </p>
      ) : null}
    </Standalone>
  );
}

/** The standalone frame, matching `InvitePage`'s — no shell chrome at all. */
function Standalone({
  children,
  role,
}: {
  readonly children: React.ReactNode;
  readonly role?: string;
}): ReactElement {
  return (
    <div className="pb-motion-content-enter flex h-screen flex-col items-center justify-center gap-3 px-6 text-center text-pb-text-secondary">
      {/* The product mark, at the top of the column on every state. It sits
          OUTSIDE the role below, so an alert announces its message and not
          the product name first. */}
      <div className="mb-3 text-pb-text">
        <Wordmark label={PRODUCT_NAME} />
      </div>
      <div
        className="flex w-full flex-col items-center gap-3"
        {...(role ? { role } : {})}
      >
        {children}
      </div>
    </div>
  );
}
